import axios from "axios";
import { handleGetRequest } from "../redux/action";
import { decryptFileWithIV } from "./FileEncryptor";
import { debugLog } from "./debugLog";

export const generateImageThumbnail = (file) => {
  return new Promise((resolve, reject) => {
    const mimeType = file.type;
    const typeCategory = mimeType.split("/")[0];

    if (typeCategory === "image") {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const MAX_WIDTH = 320;
          const scaleFactor = MAX_WIDTH / img.width;
          const width = MAX_WIDTH;
          const height = img.height * scaleFactor;

          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);
          canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.9);
        };
        img.onerror = reject;
        img.src = event.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    } else if (typeCategory === "video") {
      const video = document.createElement("video");
      video.preload = "metadata";
      video.muted = true;
      video.src = URL.createObjectURL(file);

      video.onloadedmetadata = () => {
        const seekTime = Math.min(1, video.duration / 2);
        video.currentTime = seekTime;
      };

      video.onseeked = () => {
        const width = 320;
        const height = (video.videoHeight / video.videoWidth) * width;

        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext("2d");
        ctx.drawImage(video, 0, 0, width, height);
        canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.9);
      };

      video.onerror = reject;
    } else {
      reject(
        new Error("Thumbnail generation not supported for this file type.")
      );
    }
  });
};

export const downloadFile = (file) => {
  const url = URL.createObjectURL(file);
  const a = document.createElement("a");
  a.href = url;
  a.download = file.name;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const formatBytes = (bytes) => {
  const sizes = ["Bytes", "KB", "MB", "GB"];
  if (bytes === 0) return "0 Byte";
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return parseFloat((bytes / Math.pow(1024, i)).toFixed(2)) + " " + sizes[i];
};

export const downloadAndDecryptFile = async (
  fileUrl,
  name,
  ext,
  iv,
  key,
  {
    setProgress,
    setTotalSize,
    setDownloadedSize,
    setShowDownloadModal, // 👈 add this
  }
) => {
  try {
    // ✅ OPEN MODAL IMMEDIATELY WHEN DOWNLOAD STARTS
    if (setShowDownloadModal) setShowDownloadModal(true);

    const result = await axios.get(fileUrl, {
      responseType: "blob",
      onDownloadProgress: (progressEvent) => {
        const loadedBytes = progressEvent.loaded;
        const totalBytes = progressEvent.total || 0;

        const loadedFormatted = formatBytes(loadedBytes);
        const totalFormatted = formatBytes(totalBytes);

        const percent = totalBytes
          ? ((loadedBytes / totalBytes) * 100).toFixed(2)
          : 0;

        if (setProgress) setProgress(Number(percent));
        if (setTotalSize) setTotalSize(totalFormatted);
        if (setDownloadedSize) setDownloadedSize(loadedFormatted);
      },
    });

    const encryptedBlob = result.data;

    const decryptedBlob = await decryptFileWithIV(
      encryptedBlob,
      name,
      ext,
      iv,
      key
    );

    if (!decryptedBlob)
      throw new Error("❌ Decryption failed or returned empty.");

    // ❌ DO NOT close the modal here – per your instruction
    // if (setShowDownloadModal) setShowDownloadModal(false); ❌ Don't do this
  } catch (err) {
    console.error(err);
    throw new Error("❌ Error in downloading or decrypting file.");
  }
};

export const getThumbnailBlobOrURL = async (file) => {
  try {
    const mimeType = file.type.toLowerCase();
    const fileExtension = file.name.split(".").pop().toLowerCase();

    const fetchStaticThumbnail = async (path) => {
      try {
        const response = await fetch(path);
        if (!response.ok) throw new Error("Failed to fetch thumbnail");
        const blob = await response.blob();
        return blob;
      } catch (error) {
        console.error("Error fetching static thumbnail:", error);
        return null;
      }
    };

    if (mimeType.startsWith("audio/")) {
      return await fetchStaticThumbnail(
        "/thumbnails/speaker-filled-audio-tool.png"
      );
    }

    if (mimeType.includes("contact")) {
      return await fetchStaticThumbnail(
        "/thumbnails/book-sparkles-svgrepo-com.png"
      );
    }

    if (mimeType.startsWith("application")) {
      return await fetchStaticThumbnail("/thumbnails/docs.png");
    }

    try {
      const blob = await generateImageThumbnail(file);
      return blob;
    } catch (error) {
      console.error("Error generating image thumbnail:", error);
      return null;
    }
  } catch (error) {
    console.error("Error in getThumbnailBlobOrURL:", error);
    return null;
  }
};
export function formatFileSize(bytes) {
  if (!bytes || isNaN(bytes)) return "-";

  const kb = bytes / 1024;
  const mb = kb / 1024;
  const gb = mb / 1024;

  if (gb >= 1) {
    return `${gb.toFixed(2)} GB`;
  } else if (mb >= 1) {
    return `${mb.toFixed(2)} MB`;
  } else {
    return `${kb.toFixed(2)} KB`;
  }
}
export function formatDateToYYYYMMDD(isoDate) {
  if (!isoDate) return "";

  const date = new Date(isoDate);
  if (isNaN(date.getTime())) return "";

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  return `${day}-${month}-${year}`;
}
export function gbsConverter(gbValue) {
  const bytes = gbValue * 1024 * 1024 * 1024;

  if (bytes < 1024) {
    return bytes.toFixed(2) + " Bytes";
  } else if (bytes < 1024 * 1024) {
    return (bytes / 1024).toFixed(2) + " KB";
  } else if (bytes < 1024 * 1024 * 1024) {
    return (bytes / (1024 * 1024)).toFixed(2) + " MB";
  } else {
    return (bytes / (1024 * 1024 * 1024)).toFixed(2) + " GB";
  }
}
